import api from "@/services/api.service";
import Vue from "vue";
import router from "../../router";
import { ActionTree } from "vuex";
import {
	SiteMonitorState,
	EventShareUser,
	EventDetails,
	AuditReviewResponse,
	ParkEventDTO,
	AuditRecordDTO,
	RoutingGroupDTO,
	UnlinkCameraDTO
} from "./types";
import { GPSService } from "@/services/gps.service";
import { Region, RootState } from "../types";
import CloseEventDTO from "@/types/CloseEventDTO";
import CloseMultipleEventsDTO from "@/types/CloseMultipleEventsDTO";
import CloseEventResultDTO from "@/types/CloseEventResultDTO";
import EventOutcomeDTO, { EventOutcomeResponse } from "@/types/EventOutcomeDTO";
import regionalInterceptors from "@/services/axios.regional.interceptors";
import { EventOutcome } from "@/types/reports/Filters";

export const actions: ActionTree<SiteMonitorState, RootState> = {
	async fetchEventRecords({ dispatch, commit, getters, state, rootGetters }, { eventID, reviewMode, includeGuardsWithMatchingFilter }) {
		const requestingEventRecords = getters.getRequestingEventRecords;
		if (requestingEventRecords) {
			return null;
		}
		commit("setRequestingEventRecords", true);

		let fromRecord = getters.getLatestEventRecord;

		return new Promise((resolve, reject) => {
			try {
				api.loadEventRecords(eventID, fromRecord).then(
					async events => {
						try {
							const eventDetails = state.EventDetails;

							let dispatchChange = false;

							const initialEventRecordId =
								eventDetails && eventDetails.initialEventRecordId ? eventDetails.initialEventRecordId : null;
							let initialRecord = null;

							if (!initialEventRecordId) {
								console.log("initialEventRecordId not set for event ID " + eventID);
								console.log(`Event Details: ${eventDetails}`);
							}

							if (events.data == null) {
								resolve(0);
							} else {
								const orderTabsByNewest = !!rootGetters.getFeature([
									"Alarms",
									"SiteMonitor",
									"OrderTabsByNewest"
								]);
								if (orderTabsByNewest) {
									events.data.reverse();
								}
								events.data.forEach((eventRecord: any) => {
									if (fromRecord < eventRecord.eventRecordID) {
										fromRecord = eventRecord.eventRecordID;
									}

									if (
										eventRecord.eventRecordTypeID === 48 ||
										eventRecord.eventRecordTypeID === 49 ||
										eventRecord.eventRecordTypeID === 50
									) {
										dispatchChange = true;
									}

									if (eventRecord.eventUserID != null) {
										const userShare = getters.userShares[eventRecord.eventUserID];

										if (userShare != null) {
											eventRecord.colorIndex = userShare.colorIndex;
											eventRecord.initials = userShare.initials;
											eventRecord.userName = userShare.name;
											eventRecord.userId = userShare.userID;
										} else {
											Vue.set(eventRecord, "colorIndex", null);
											Vue.set(eventRecord, "initials", null);
											Vue.set(eventRecord, "userName", null);
											Vue.set(eventRecord, "userId", null);

											commit("setEventSharesSet", false);
										}
									}

									if (initialEventRecordId != null) {
										if (eventRecord.eventRecordID === initialEventRecordId) {
											console.log("Set initial event record from event record refresh");
											initialRecord = eventRecord;
										}
									}
								});

								if (dispatchChange && !reviewMode) {
									if (eventDetails && eventDetails.latLong && eventDetails.latLong !== "") {
										let latLong = eventDetails.latLong;
										const IsLatLongInCoordinateFormat = /^-?[\d.]+[NS]? -?[\d.]+[WE]?($|,)/.test(latLong);
										if (!IsLatLongInCoordinateFormat) {
											// Look up the latLong value
											const geoCoder = new GPSService();
											const response = await geoCoder.GeoCodeLookup(latLong, rootGetters.getMapKey);

											// Any location results associated with provided latLong value?
											if (response.results && response.results.length > 0) {
												const addressGeometryLocation = response.results[0].geometry.location;
												latLong = `${addressGeometryLocation.lat} ${addressGeometryLocation.lng}`;
											}
										}

										dispatch(
											"dispatch/updateDispatchedGuards",
											{
												eventId: eventID,
												location: latLong,
												includeGuardsWithMatchingFilter: includeGuardsWithMatchingFilter
											},
											{
												root: true
											}
										);
									}
								}

								commit("setLatestEventRecordID", fromRecord);

								commit("setEventRecords", { eventRecords: events.data, orderTabsByNewest });

								commit("setDataError", false, { root: true });

								if (initialRecord) {
									commit("setSelectedEventRecord", initialRecord);
								}
								commit("setRequestingEventRecords", false);

								resolve(events.data.length);
							}
						}
						catch (err) {
							if (err.response != null && err.response.status === 401) {
								router.push({ path: "/event-queue" });
							} else if (err.response != null && err.response.status === 403) {
								commit("setEventNoPermission", true);
							} else {
								commit("setDataError", true, { root: true });
								resolve(0);
							}
							commit("setRequestingEventRecords", false);
						}
					},
					errorResponse => {
						if (errorResponse.response != null && errorResponse.response.status === 401) {
							router.push({ path: "/event-queue" });
						} else if (errorResponse.response != null && errorResponse.response.status === 403) {
							commit("setEventNoPermission", true);
						} else {
							commit("setDataError", true, { root: true });
							resolve(0);
						}
						commit("setRequestingEventRecords", false);
					}
				);
			}
			catch (err) {
				if (err.response != null && err.response.status === 401) {
					router.push({ path: "/event-queue" });
				} else if (err.response != null && err.response.status === 403) {
					commit("setEventNoPermission", true);
				} else {
					commit("setDataError", true, { root: true });
					resolve(0);
				}
				commit("setRequestingEventRecords", false);
			}
		});
	},
	async ackEventRecord({ commit }, eventRecord) {
		if (eventRecord && eventRecord.awaitingAcknowledgement) {
			await api.eventRecordAcknowledgement(eventRecord.eventRecordID);
			commit("ackEventRecord", eventRecord.eventRecordID);
		}
	},
	async updateSharing({ commit, getters }, { eventID }) {
		const data = await api.loadShareForEvent(eventID);

		const myUserID: number = data.myUserID;
		const controllerUserID: number = data.controllerUserID;
		const shareUsers: EventShareUser[] = data.users;

		shareUsers.forEach(share => {
			share.isController = share.userID === controllerUserID;
		});

		commit("setIsController", controllerUserID === myUserID);
		commit("setUserID", myUserID);
		commit("setControllerUserID", controllerUserID);
		commit("setEventShare", shareUsers);

		if (!getters.getEventShareSet) {
			const userShares = getters.userShares;

			commit("setEventRecordShares", userShares);
			commit("tasks/updateTaskAssignedToEventUser", userShares, {
				root: true
			});
			commit("setEventSharesSet", true);
		}
	},
	async removeShare({ commit }, { eventID, eventUserID }) {
		await api.eventShareRemove(eventID, eventUserID);

		const shareUsers = await api.loadShareForEvent(eventID);
		commit("setEventShare", shareUsers.users);
	},
	async fetchEventDetails({ commit, state, rootGetters }, eventId: number) {
		const details = await api.loadEventDetails(eventId);

		if (details.data.initialEventRecordId && !state.SelectedEventRecord) {
			console.log("Setting SelectedEventRecord from event details");

			const initialRecord = state.EventRecords.find(
				(record: any) => record.eventRecordID === details.data.initialEventRecordId
			);

			if (initialRecord) {
				commit("setSelectedEventRecord", initialRecord);
			}
		}

		commit("setEventDetails", details.data);

		return details.data;
	},

	setEventDetails({ commit }, data: EventDetails | null) {
		commit("setEventDetails", data);
	},

	/**
	 * Gets the details of an event to be shown in an expanded event record.
	 * @param context Vuex context.
	 * @param eventRecordID The id of the event record that will be expanded.
	 */
	async getEventRecordDetails(context, { eventRecordId }) {
		const result = await api.loadEventRecordDetails(eventRecordId);
		return result;
	},

	async setEventRecordFilter({ commit }, id: number) {
		commit("setEventRecordFilter", id);
	},

	async eventRecordAcknowledgement(context, eventRecordID: number) {
		const response = await api.eventRecordAcknowledgement(eventRecordID);
		return response.data;
	},

	/**
	 * Creates an event record attached to an event. The current user must be a part
	 * of the event, otherwise an error will be returned.
	 * @param context Vuex context.
	 * @param data The data to include with the new event record.
	 */
	async createAuditRecord(context, payload: AuditRecordDTO) {
		const { data } = await api.postAudit(payload);
		return data;
	},

	/**
	 * Like createAuditRecord, creates an event record with details attached to an event,
	 * but does not require that the current user already be a part of the event.
	 * @param context Vuex context.
	 * @param param1 The eventId, eventRecordTypeId, and any details (string) to include with the new event record.
	 */
	async createReviewRecord(context, payload: AuditRecordDTO): Promise<AuditReviewResponse> {
		const response = await api.auditReviewAction(payload);
		return response.data;
	},

	async loadRoutingGroupsByEvent(context, eventID: number) {
		const response = await api.loadRoutingGroupsByEvent(eventID);
		return response.data;
	},

	async loadOutcomesForEvent({ commit }, eventID: number): Promise<EventOutcomeResponse> {
		const response = await api.loadOutcomesForEvent(eventID);
		if (response.status != 200){
			return null;
		}
		commit("setEventOutcomes", response.data)
		return response.data;
	},

	async loadParkedNoteOutcomesForEvent(context, eventID: number): Promise<EventOutcomeDTO[]> {
		return await api.loadParkedNoteOutcomesForEvent(eventID);
	},

	async loadEventOutcomesForAnyAlarm(context): Promise<EventOutcome[]> {
		const response = await api.fetchEventOutcomesForAnyAlarm();
		return response;
	},

	async loadOutcomesForUser(context, eventID: number) {
		const response = await api.fetchEventOutcomesForUser();
		return response.data;
	},

	async changeEventRoutingGroup(context, data: RoutingGroupDTO) {
		const response = await api.changeEventRoutingGroup(data);
		return response.data;
	},

	async loadMaskReasonsForAlarm({ commit }, groupID: number) {
		const responseData = await api.loadMaskReasonsForAlarm(groupID);
		commit("setMaskReasons", responseData);
	},

	async loadMaskReasonsForAlarmWithRegion({ commit }, { groupID, regionURL }) {
		const responseData = await api.loadMaskReasonsForAlarm(groupID, regionURL);
		commit("setMaskReasons", responseData);
	},

	async loadMaskReasonsForGroup({ commit }, payload: { groupID: number; regionUrl?: string }) {
		const responseData = await api.loadMaskReasonsForGroup(payload.groupID, payload.regionUrl);
		commit("setGroupMaskReasons", responseData);
	},

	async loadMaskReasonsForGroupWithRegion({ commit }, { groupID, regionURL }) {
		const responseData = await api.loadMaskReasonsForGroup(groupID, regionURL);
		commit("setGroupMaskReasons", responseData);
	},

	async maskAlarm(context, { maskRequest, regionURL }) {
		return await api.maskAlarm(maskRequest, regionURL);
	},

	async loadMaskedAlarms({ commit }, { groupID, regionURL }) {
		const response = await api.loadMaskedAlarmsForGroup(groupID, regionURL);
		commit("setMaskedAlarms", response.data);
	},

	async closeEvent(context, data: CloseEventDTO): Promise<CloseEventResultDTO> {
		const response = await api.closeEvent(data);
		return response;
	},

	async forceCloseMultipleEvents(context, data: CloseMultipleEventsDTO): Promise<CloseEventResultDTO> {
		const response = await api.forceCloseMultipleEvents(data);
		return response;
	},

	async loadEventGroup(context, groupID: number) {
		return await api.loadGroup(groupID);
	},

	async loadExternalUsersAllowed({ commit }, eventId: number) {
		const allow = await api.loadExternalUserShareFlag(eventId);
		commit("setExternalUsersAllowed", allow);
	},

	async searchGroup(context, SearchTerm: string) {
		return await api.searchGroup(SearchTerm);
	},

	setAreaNotesVisible({ commit }, isVisible: boolean) {
		commit("setAreaNotesVisible", isVisible);
	},

	async loadEventOperators({ commit }, eventID: number) {
		const shareUsers = await api.loadShareForEvent(eventID);
		commit("setEventShare", shareUsers.users);
	},

	async loadHistoricalEventOperators({ commit }, eventID: number) {
		const shareUsers = await api.loadHistoricalShareForEvent(eventID);
		commit("setEventShare", shareUsers.users);
	},

	async eventShareSearch({ commit }, query: string) {
		const { data } = await api.eventShareSearch(query);
		commit("setShareWithPersons", data);
	},

	async loadNotificationTemplate(context, payload: any) {
		const { data } = await api.loadNotificationTemplate(payload);
		return data;
	},

	async addAdhocEventShare({ commit }, payload: any) {
		const { data } = await api.addAdhocEventShare(payload);
		const shareLink = window.location.toString().replace(window.location.hash, "#/Access/" + data.shareID);

		commit("setAdHocShareDetails", {
			eventUserID: data.shareID,
			accessCode: data.accessCode,
			shareLink
		});
		data.shareLink = shareLink;

		return data;
	},

	async shareEventWithUser(context, payload: any) {
		return await api.shareEventWithUser(payload);
	},

	async shareEventWithGroup(context, payload: any) {
		return await api.shareEventWithGroup(payload);
	},

	async eventPassControl(context, { eventID, userID }) {
		return await api.EventPassControl(eventID, userID);
	},

	async parkEvent(context, payload: ParkEventDTO) {
		return await api.EventPark(payload);
	},

	async leaveEvent(context, eventID: number) {
		return await api.leaveEvent(eventID);
	},

	async eventRecordAllAcknowledgement(context, eventID: number) {
		return await api.eventRecordAllAcknowledgement(eventID);
	},

	async unlinkCamera(context, data: UnlinkCameraDTO) {
		return await api.unlinkCamera(data);
	},

	async createResponseActionForEventRecord({ commit }, { eventRecordId, title, priority, groupId }) {
		const responseID = await api.createResponseActionForEventRecord({
			eventRecordId,
			title,
			priority,
			groupId
		});

		commit("setEventRecordResponse", {
			eventRecordID: eventRecordId,
			title,
			priority,
			responseID
		});
	},

	/**
	 * Flags the current event for review and returns true/false
	 * depending on the success of the operation.
	 * @param eventId The ID of the event currently being processed.
	 */
	async flagEventForReview({ commit }, { eventId, flagged }) {
		const result = await api.flagEventForReview(eventId, flagged);

		if (result) {
			commit("setFlaggedForReview", flagged);
		}

		return result;
	},

	/**
	 * preserves the current event and returns true/false
	 * depending on the success of the operation.
	 * @param eventId The ID of the event currently being processed.
	 */
	async preserveEvent({ commit }, { eventId, preserved }) {
		const result = await api.preserveEvent(eventId, preserved);
		return result;
	},

	/**
	 * Puts an existing event into on test mode.
	 * @param context Vuex context
	 * @param param1 The necessary info to create the payload
	 */
	async putEventOnTest(context, { eventId, groupId, hours, minutes, date, scheduleType, auditMode, onTestNote }) {
		const result = await api.putEventOnTest({
			eventId,
			groupId,
			hours,
			minutes,
			date,
			scheduleType,
			auditMode,
			onTestNote
		});
	},

	async fetchEventRecordDetails({ commit }, { eventRecordID }) {
		const result = await api.loadEventRecordDetails(eventRecordID);
		return result;
	},

	async removeAdhocUsersFromEvent({ commit, state }) {
		let adhocUsers = state.EventShares.filter((user: any) => user.userID == null && !user.removed && user.isActive);

		adhocUsers.forEach(adhocUser => {
			api.eventShareRemove(adhocUser.eventID, adhocUser.eventUserID);
		});
	},

	setHideMapFlag({ commit }, hideMapFlag: boolean) {
		commit("setHideMapFlag", hideMapFlag);
	},

	async updateEventRecordLocation({ commit }, { eventRecordId, latLng, elevation }) {
		if (elevation === undefined) elevation = null;
		await api.updateEventRecordLocation(eventRecordId, latLng, elevation);
		commit("setEventRecordLocation", { eventRecordId: eventRecordId, latLng, elevation });
	},

	/**
	 * Updates the event region and sets up axios to use the remote data service.
	 * @param param0 Vuex store
	 * @param param1 The region id and url
	 */
	async updateEventRegion({ rootState, commit, dispatch }, { regionId, regionUrl }) {
		// If we're setting the active region back to 0, and we've got
		// the original (local) user login cached, then restore it.
		if (regionId == 0 && rootState.localUserData) {
			commit("resetUserData", null, {
				root: true
			});
		}

		commit("setEventRegion", {
			regionId,
			regionUrl
		});

		await dispatch("applyEventRegion");
	},

	/**
	 * Applies the current event region to axios.
	 * @param param0 Vuex store
	 */
	async applyEventRegion({ state }) {
		// Update the base URL
		api.setBaseApiUrl(state.eventRegion.regionUrl);
	},

	/**
	 * Resets the current region to the original (local) one
	 * @param param0 Vuex store
	 */
	async resetRegion({ dispatch }) {
		await dispatch("updateEventRegion", {
			regionId: 0,
			regionUrl: api.getOriginalApiUrl
		});
	}
};
